import { map, take } from "rxjs";
import { ChangeDetectionStrategy, Component, EventEmitter, Input, type OnInit, Output, signal, TemplateRef, ViewChild } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { TranslateService } from "@ngx-translate/core";
import type { MenuItem } from "primeng/api";
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatDesigns from "@medsurf/flat-designs";
import * as FlatFacades from '@medsurf/flat-facades';
import * as FlatServices from '@medsurf/flat-services';
import { ZodSharedValidator } from '@medsurf/flat-validators';
import { ModalControlComponent } from "@medsurf/flat-components";
import { MediaSidebarComponent } from "./media-sidebar/media-sidebar.component";

@Component({
    selector: 'medsurf-common-media-library',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './media-library.component.html',
    styleUrls: ['./media-library.component.scss'],
    standalone: false
})
export class MediaLibraryComponent implements OnInit {
  @Input() allowedTypes: FlatModels.MediaEntityModels.MediaType[] = [
    FlatModels.MediaEntityModels.MediaType.enum.MediaDocument,
    FlatModels.MediaEntityModels.MediaType.enum.MediaImage,
    FlatModels.MediaEntityModels.MediaType.enum.MediaDeepzoom,
    FlatModels.MediaEntityModels.MediaType.enum.MediaVideo,
    FlatModels.MediaEntityModels.MediaType.enum.MediaAudio
  ];
  @Output() uploadChange = new EventEmitter<boolean>();
  
  @ViewChild('searchStringInputText', {static: false}) public searchStringInputText: FlatDesigns.TextInputFormControlDesign;
  @ViewChild('media_sidebar', {static: false}) public mediaSidebar: MediaSidebarComponent;
  @ViewChild('confirm_delete_template') public confirmDelete: TemplateRef<any>;
  
  #isSubmitted = signal(false);
  isUploading = signal(false);
  isModal = signal(false);
  #handled = false;

  mediaSearchForm = new FormGroup({
    searchString: new FormControl(''),
    typeFilter: new FormControl(this.allowedTypes),
    uploadedByMe: new FormControl(false),
    usedInFilter: new FormControl<string[]>([]),
    sortField: new FormControl<FlatModels.SearchControlModels.MediaSearch['sortField']>(null),
    sortOrder: new FormControl(0),
    rows: new FormControl(25),
    first: new FormControl(0),
    isDuplicate: new FormControl(false),
    selectedIds: new FormControl<string[]>([]),
    isUnused: new FormControl(false)
  }, {
    updateOn: 'change',
    validators: ZodSharedValidator.validate(
      FlatModels.SearchControlModels.MediaSearchSchema.pick({
        searchString: true,
        typeFilter: true,
        uploadedByMe: true,
        usedInFilter: true,
        sortField: true,
        sortOrder: true,
        rows: true,
        first: true,
        isDuplicate: true,
        selectedIds: true,
        isUnused: true
      })
    )
  })

  editMenuItems$ = this.searchControlFacade.currentSelectedMedia$.pipe(map((media) => {
    const mediaUsedIn = this.mediaAuthorFacade.snapshot_mediaUsedIn();
    return [
			{
				label: this.translate.instant('global.download_original_file'),
				icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.DOWNLOAD,
				disabled: this.mediaControlService.isDeepzoom(media),
				command: () => this.downloadMedia(media),
			},
			{
				label: this.translate.instant('global.image_exchange'),
				icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.SHUFFLE,
        disabled: this.mediaControlService.isDeepzoom(media),
				command: () => this.replaceFile(media),
			},
      {
        label: this.translate.instant('global.copy_media_link'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.COPY,
        command: () => this.copyMediaUrl(media),
      },
			{
				separator: true
			},
			{
				label: this.translate.instant('global.media_remove'),
        disabled: mediaUsedIn[media?.id]?.length > 0 || false,
				icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.REMOVE,
				styleClass: FlatModels.ButtonDesignModels.ButtonDesignClass.MENU_DANGER,
				command: () => this.removeFile(media),
			}
		] satisfies MenuItem[];
  }));

	getMediasMenuItems = () => {
		const mediaSet = this.bulkEditAuthorFacade.snapshot_mediaSet();
		const hasUsedIn = this.bulkEditAuthorFacade.snapshot_hasUsedIn();
		const disabled = mediaSet.size === 0;
		return [
			{
				label: this.translate.instant('global.deselect'),
				disabled: disabled,
				icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.SELECTION_REMOVE,
				command: () => this.deselectMedias(),
			},
			{
				label: this.translate.instant('global.remove'),
				disabled: disabled || hasUsedIn,
				icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.REMOVE,
				styleClass: FlatModels.ButtonDesignModels.ButtonDesignClass.MENU_DANGER,
				command: () => this.bulkRemoveMedia(),
			}
		] satisfies MenuItem[];
	}
  readonly FlatModels = FlatModels;
  
  public constructor(public bulkEditAuthorFacade: FlatFacades.BulkEditAuthorFacade,
                     public slideAuthorFacade: FlatFacades.SlideAuthorFacade,
                     public nodeSharedFacade: FlatFacades.NodeSharedFacade,
                     public mediaAuthorFacade: FlatFacades.MediaAuthorFacade,
                     public searchControlFacade: FlatFacades.SearchControlFacade,
                     public appSharedFacade: FlatFacades.AppSharedFacade,
                     public mediaControlService: FlatServices.MediaControlService,
                     public dynamicDialogRef: DynamicDialogRef,
                     public dynamicDialogConfig: DynamicDialogConfig<FlatModels.ModalAuthorModels.MediaLibraryModalData>,
                     private apiControlService: FlatServices.ApiControlService,
                     private dialogService: DialogService,
                     public translate: TranslateService,
                     private alertControlFacade: FlatFacades.AlertControlFacade,
                     private clipboardAuthorFacade: FlatFacades.ClipboardAuthorFacade) {

    if (this.dynamicDialogConfig?.data?.allowedTypes) {
      this.allowedTypes = [...this.dynamicDialogConfig.data.allowedTypes]
      this.mediaSearchForm.controls.typeFilter.setValue(this.allowedTypes)
    }
    if (this.dynamicDialogConfig?.data?.isModal) {
      this.isModal.set(this.dynamicDialogConfig.data.isModal);
    }

    if (this.dynamicDialogConfig?.data?.selected) {
      this.mediaSearchForm.controls.selectedIds.setValue([this.dynamicDialogConfig.data.selected.id]);

      this.searchControlFacade.requestSetCurrentSelectedMediaResult(
        this.dynamicDialogConfig.data.selected as FlatModels.SearchControlModels.MediaData
      );
    } else {
      this.searchControlFacade.requestResetCurrentSelectedMediaResult();
    }
  }

  ngOnInit(): void {
    this.onSubmit();
    
    // Autofocus
    setTimeout(() => {
      this.searchStringInputText?.inputElement?.nativeElement?.focus();
    });
  }
  
  onSubmit(resetPage = true) {
    if (this.#isSubmitted() || this.mediaSearchForm.invalid) {
      return;
    }
    this.#isSubmitted.set(true);

    if (resetPage) this.mediaSearchForm.controls.first.setValue(0);
    
    const search: FlatModels.SearchControlModels.MediaSearch = {
      searchString: this.mediaSearchForm.value.searchString || "",
      typeFilter: this.mediaSearchForm.value.typeFilter || this.allowedTypes,
      uploadedByMe: this.mediaSearchForm.value.uploadedByMe || false,
      usedInFilter: this.mediaSearchForm.value.usedInFilter || [],
      sortField: this.mediaSearchForm.value.sortField || null,
      sortOrder: this.mediaSearchForm.value.sortOrder || 0,
      rows: this.mediaSearchForm.value.rows || 25,
      first: this.mediaSearchForm.value.first || 0,
      isDuplicate: this.mediaSearchForm.value.isDuplicate || false,
      selectedIds: this.mediaSearchForm.value.selectedIds || [],
      isUnused: this.mediaSearchForm.value.isUnused || false,
    };
    this.searchControlFacade.requestFindMedia(search).subscribe({
      error: (result) => {
        ZodSharedValidator.handleError(result, this.alertControlFacade, this.mediaSearchForm);
        this.#isSubmitted.set(false);
      },
      next: () => {
        this.#isSubmitted.set(false);
      }
    });
  }

  changeLayout(event: any) {
    this.slideAuthorFacade.requestSetLayout(event.layout).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }
  
  onLazy(event: any){
    this.mediaSearchForm.controls.first.setValue(event?.first);
  }
  
  onMediaClick(event: MouseEvent,
               mediaResult: FlatModels.SearchControlModels.MediaData) {
    if (this.allowedTypes?.[0] && !this.allowedTypes.includes(mediaResult.type as FlatModels.MediaEntityModels.MediaType)) {
      return;
    }
    if (this.#handled) {
      this.#handled = false;
      return;
    }
    if (event.ctrlKey || event.metaKey) {
      return this.bulkEditAuthorFacade.requestToggleMedia(mediaResult.id).subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
      });
    } else {
      this.searchControlFacade.requestSetCurrentSelectedMediaResult(mediaResult).subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
      });
    }
  }
  
  toggleIsUploading() {
    this.isUploading.set(!this.isUploading());
    if (!this.isUploading()) {
      this.onSubmit();
    }
  }
  
  finishIsUploading(media: FlatModels.MediaEntityModels.MediaEntityType) {
    this.isUploading.set(false);
    this.mediaSearchForm.controls.selectedIds.setValue([media.id]);
    this.searchControlFacade.ofActionDispatchedSearchSuccess.pipe(take(1)).subscribe(() => {
      this.searchControlFacade.requestSetCurrentSelectedMediaResultByMedia(media).subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
      });
    });
    this.onSubmit();
  }
  
  mediaOnResizeStart() { // event: SplitterResizeStartEvent) {
    this.slideAuthorFacade.requestSetIsSplitterMoving(true).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
    this.slideAuthorFacade.requestCollapseMedia(false, true).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }
  
  mediaOnResizeEnd(event: { sizes: number[] }) {
    this.slideAuthorFacade.requestSetIsSplitterMoving(false).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
    this.slideAuthorFacade.requestSetMediaPanelSize(event.sizes).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }
  
  mediaCollapsedChange(mediaIsMinimized: boolean) {
    this.slideAuthorFacade.requestCollapseMedia(mediaIsMinimized).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }
  
  deselectMedias() {
    this.bulkEditAuthorFacade.requestDeselectMedias().subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }
  
  bulkRemoveMedia() {
    const dialogRef = this.dialogService.open(ModalControlComponent, {
      header: this.translate.instant('global.images_remove'),
      data: {
        inputLabel: 'global.remove',
        inputClass: FlatModels.ButtonDesignModels.ButtonDesignClass.DANGER,
        template: this.confirmDelete,
      },
      dismissableMask: true,
      width: '50%'
    });
    dialogRef.onClose.subscribe(remove => {
      if (remove) {
        this.bulkEditAuthorFacade.requestRemoveBulkEditMedias().subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
          next: () => {
            this.onSubmit();
          }
        });
      }
    });
  }
  
  onToggleBulkMedia(id: string) {
    this.#handled = true;
    this.bulkEditAuthorFacade.requestToggleMedia(id).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }
  
  trackBy(index: number,
          item: FlatModels.MediaEntityModels.MediaEntityType) {
    return item.id || index;
  }
  
  trackByUsedIn(index: number,
                item: FlatModels.MediaAuthorModels.MediaUsedInResultEntry) {
    return item.id || index;
  }
  
  mediaTrackBy(index: number,
               item: FlatModels.MediaEntityModels.Media){
    return item.id || index;
  }
  
  downloadMedia(media: FlatModels.MediaEntityModels.Media) {
    const url = this.mediaControlService.getDownloadUrl(media, true);
    window.open(url, '_blank');
  }

  async copyMediaUrl(media: FlatModels.MediaEntityModels.Media) {
    const url = this.mediaControlService.getEditorUrl(media);
    await this.clipboardAuthorFacade.requestCopyToClipboard(url);
  }
  
  replaceFile(media: FlatModels.MediaEntityModels.Media) {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = media.mime;
    input.addEventListener('change', async () => {
      if (input.files && input.files.length > 0) {
        const file = input.files[0];
        this.apiControlService.createMediaUpload({
          file,
          blockUi: true,
          action: 'uploadMedia',
          mediaId: media.id,
          onError: (error) => {
            this.alertControlFacade.requestAddError(error)
          },
          onSuccess: async () => {
            this.onSubmit();
          }
        })
      }
    });
    input.click();
  }

  removeFile(media: FlatModels.MediaEntityModels.Media) {
    const dialogRef = this.dialogService.open(ModalControlComponent, {
      header: this.translate.instant('global.media_remove'),
      data: {
        inputLabel: 'global.remove',
        inputClass: FlatModels.ButtonDesignModels.ButtonDesignClass.DANGER,
        template: this.confirmDelete,
      },
      dismissableMask: true,
      width: '50%'
    });
    dialogRef.onClose.subscribe(remove => {
      if(remove) {
        this.mediaAuthorFacade.requestRemoveMedia([media.id]).subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
          next: () => this.onSubmit()
        })
      }
    });
  }
}
